import { get } from 'lodash';
import * as React from 'react';
import { RequestManager } from '../../../../../utilities/request';
import { Spinner } from '../../../../ui-components/spinner/spinner';
import './featured-image-carousel.scss';

const IMAGES_PER_PAGE = 3;

interface FeaturedImageCarouselProps {
	pictures: any[];
}

export class FeaturedImageCarousel extends React.Component<FeaturedImageCarouselProps, any> {
	public state: any = {
		images: [],
		loading: true,
		currentPage: 0,
		lightboxImage: null,
	};

	public async componentDidMount(): Promise<void> {
		await this.fetchImages();
	}

	public async fetchImages(): Promise<void> {
		const pictures = this.props.pictures || [];

		if (!pictures.length) {
			this.setState({ loading: false });
			return;
		}

		const images: any[] = [];

		for (const picture of pictures) {
			try {
				const aid = get(picture, 'aid');

				if (!aid) {
					continue;
				}

				const response = await RequestManager.get(`/free/files/${aid}/info`);

				if (response) {
					images.push({ ...picture, ...response });
				}
			} catch {
				continue;
			}
		}

		this.setState({ images, loading: false });
	}

	public get totalPages(): number {
		return Math.ceil(this.state.images.length / IMAGES_PER_PAGE);
	}

	public get visibleImages(): any[] {
		const start = this.state.currentPage * IMAGES_PER_PAGE;
		return this.state.images.slice(start, start + IMAGES_PER_PAGE);
	}

	public goToPage(page: number): void {
		this.setState({ currentPage: page });
	}

	public prevPage(): void {
		if (this.state.currentPage > 0) {
			this.setState({ currentPage: this.state.currentPage - 1 });
		}
	}

	public nextPage(): void {
		if (this.state.currentPage < this.totalPages - 1) {
			this.setState({ currentPage: this.state.currentPage + 1 });
		}
	}

	public render(): React.ReactElement | null {
		if (this.state.loading) {
			return (
				<div className='FeaturedImageCarousel'>
					<Spinner size='small' />
				</div>
			);
		}

		if (!this.state.images.length) {
			return null;
		}

		const showArrows = this.totalPages > 1;

		return (
			<div className='FeaturedImageCarousel'>
				{showArrows && (
					<div
						className='CarouselArrow LeftArrow'
						onClick={() => this.prevPage()}
						style={{ visibility: this.state.currentPage > 0 ? 'visible' : 'hidden' }}
					>
						<span className='fas fa-chevron-left palette--c-neutral-5' style={{ fontSize: '12px' }} />
					</div>
				)}

				<div className='CarouselTrack'>
					{this.visibleImages.map((image, i) => (
						<div
							key={get(image, 'aid') || i}
							className='CarouselImage'
							onClick={() => this.setState({ lightboxImage: get(image, 'url') || get(image, 'thumbnail') })}
						>
							<img
								src={get(image, 'url') || get(image, 'thumbnail')}
								alt={get(image, 'description') || 'provider image'}
							/>
						</div>
					))}
				</div>

				{showArrows && (
					<div
						className='CarouselArrow RightArrow'
						onClick={() => this.nextPage()}
						style={{ visibility: this.state.currentPage < this.totalPages - 1 ? 'visible' : 'hidden' }}
					>
						<span className='fas fa-chevron-right palette--c-neutral-5' style={{ fontSize: '12px' }} />
					</div>
				)}

				{this.totalPages > 1 && (
					<div className='CarouselDots'>
						{Array.from({ length: this.totalPages }).map((_, i) => (
							<div
								key={i}
								className={`Dot ${i === this.state.currentPage ? 'Active' : ''}`}
								onClick={() => this.goToPage(i)}
							/>
						))}
					</div>
				)}

				{this.state.lightboxImage && (
					<div className='LightboxOverlay' onClick={() => this.setState({ lightboxImage: null })}>
						<span className='LightboxClose fas fa-times' onClick={() => this.setState({ lightboxImage: null })} />
						<img
							className='LightboxImage'
							src={this.state.lightboxImage}
							alt='Full size'
							onClick={(e) => e.stopPropagation()}
						/>
					</div>
				)}
			</div>
		);
	}
}
